import React, { useEffect, useRef, useState } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { api } from "../../lib/api";

const AC_COLOR = "#4CC9F0";
const KIND_COLORS = {
  tanker: "#F2542D",
  helicopter: "#4CC9F0",
  "lead/recon": "#FFD60A",
  smokejumper: "#B388EB",
  "fire agency": "#2A9D8F",
  "near fire": "#979F91",
};

// hex -> photo result cache (session)
const _photoCache = {};

function AircraftPhoto({ hex, reg }) {
  const [photo, setPhoto] = useState(_photoCache[hex]);

  useEffect(() => {
    if (!hex || _photoCache[hex] !== undefined) return;
    let alive = true;
    api.get(`/aircraft/photo/${hex}`, { params: reg ? { reg } : {} })
      .then((r) => {
        _photoCache[hex] = r.data.photo || null;
        if (alive) setPhoto(_photoCache[hex]);
      })
      .catch(() => {
        _photoCache[hex] = null;
        if (alive) setPhoto(null);
      });
    return () => { alive = false; };
  }, [hex, reg]);

  if (photo === undefined) return null;
  if (photo === null) {
    return (
      <div
        data-testid={`aircraft-no-photo-${hex}`}
        style={{ fontSize: 10, opacity: 0.55, margin: "4px 0 2px", fontStyle: "italic" }}
      >
        No photo available for this airframe
      </div>
    );
  }
  return (
    <a
      href={photo.link || "#"}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`aircraft-photo-${hex}`}
      style={{ display: "block", margin: "6px 0 2px" }}
    >
      <img
        src={photo.src}
        alt="Aircraft"
        style={{ width: "100%", borderRadius: 3, display: "block", border: "1px solid rgba(255,255,255,0.15)" }}
        loading="lazy"
      />
      {photo.photographer && (
        <span style={{ fontSize: 9, opacity: 0.6 }}>© {photo.photographer} · planespotters.net</span>
      )}
    </a>
  );
}

const _iconCache = {};
function planeIcon(track, kind) {
  const rot = Math.round((track || 0) / 5) * 5;
  const heli = kind === "helicopter";
  const key = `${rot}-${heli}`;
  if (!_iconCache[key]) {
    const body = heli
      ? `<circle cx="12" cy="12" r="4.5" fill="${AC_COLOR}" stroke="#0A0D0C" stroke-width="1.4"/>
         <rect x="2" y="10.9" width="20" height="2.2" rx="1.1" fill="${AC_COLOR}" stroke="#0A0D0C" stroke-width="0.8" transform="rotate(45 12 12)"/>
         <rect x="2" y="10.9" width="20" height="2.2" rx="1.1" fill="${AC_COLOR}" stroke="#0A0D0C" stroke-width="0.8" transform="rotate(-45 12 12)"/>`
      : `<path d="M12 1.8 L13.6 8.2 L22 12.2 L22 14.4 L13.6 12.2 L13.4 18 L16.2 20.4 L16.2 22.2 L12 21 L7.8 22.2 L7.8 20.4 L10.6 18 L10.4 12.2 L2 14.4 L2 12.2 L10.4 8.2 Z"
         fill="${AC_COLOR}" stroke="#0A0D0C" stroke-width="1.2" stroke-linejoin="round"/>`;
    _iconCache[key] = L.divIcon({
      className: "ac-smooth",
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
        style="transform: rotate(${heli ? 0 : rot}deg); filter: drop-shadow(0 1px 2px rgba(0,0,0,0.65));">${body}</svg>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -13],
    });
  }
  return _iconCache[key];
}

const EARTH_R = 6371000;

/** Dead-reckon a position along track using ground speed and elapsed time. */
function extrapolate(a, baseMs, nowMs) {
  if (a.on_ground || a.gs_kt == null || a.gs_kt < 30 || a.track == null) {
    return [a.latitude, a.longitude];
  }
  // position age = time since our fetch + how stale it already was upstream,
  // capped so a lost signal doesn't fly the icon off into the distance
  const age = Math.min((nowMs - baseMs) / 1000 + (a.seen_s || 0), 60);
  const dist = a.gs_kt * 0.514444 * age; // meters
  const brg = (a.track * Math.PI) / 180;
  const lat1 = (a.latitude * Math.PI) / 180;
  const dLat = (dist * Math.cos(brg)) / EARTH_R;
  const dLon = (dist * Math.sin(brg)) / (EARTH_R * Math.cos(lat1));
  return [a.latitude + (dLat * 180) / Math.PI, a.longitude + (dLon * 180) / Math.PI];
}

/** Re-render every second so markers glide between 15 s data polls. */
function useSmoothTick(aircraft) {
  const baseMsRef = useRef(Date.now());
  const [, setTick] = useState(0);
  useEffect(() => { baseMsRef.current = Date.now(); }, [aircraft]);
  useEffect(() => {
    if (!aircraft.length) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [aircraft.length]);
  return baseMsRef;
}

function AltitudeTrend({ rate }) {
  if (rate == null || Math.abs(rate) < 300) return null;
  const up = rate > 0;
  return (
    <span style={{ color: up ? "#2A9D8F" : "#F2542D", fontWeight: 700 }}>
      {" "}{up ? "▲" : "▼"} {Math.abs(Math.round(rate)).toLocaleString()} fpm
    </span>
  );
}

function SignalAge({ seenS, baseMs }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const total = Math.round((Date.now() - baseMs) / 1000 + (seenS || 0));
  return (
    <span style={{ color: total > 60 ? "#E63946" : "#2A9D8F" }}>
      signal {total}s ago
    </span>
  );
}

export default function AircraftLayer({ aircraft = [] }) {
  const baseMsRef = useSmoothTick(aircraft);
  const now = Date.now();
  return (
    <>
      {aircraft.map((a) => {
        const kindColor = KIND_COLORS[a.kind] || AC_COLOR;
        return (
          <Marker
            key={a.hex}
            position={extrapolate(a, baseMsRef.current, now)}
            icon={planeIcon(a.track, a.kind)}
          >
            <Popup>
              <div style={{ minWidth: 220 }} data-testid={`aircraft-popup-${a.hex}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{
                    fontWeight: 700, color: kindColor, fontSize: 11,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    border: `1px solid ${kindColor}`, borderRadius: 2, padding: "1px 6px",
                  }}>
                    {a.kind || "aircraft"}
                  </span>
                  <span style={{ fontSize: 10, opacity: 0.65 }}>Fire Aviation</span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, margin: "4px 0 2px" }}>
                  {a.callsign || a.registration || a.hex}
                </div>
                <AircraftPhoto hex={a.hex} reg={a.registration} />
                <div style={{ fontSize: 12, lineHeight: 1.55 }}>
                  {a.desc || a.type || "Unknown type"}
                  {a.operator && (
                    <>
                      <br />
                      <span style={{ fontWeight: 600 }}>{a.operator}</span>
                    </>
                  )}
                  <br />
                  {a.on_ground
                    ? "On ground"
                    : a.alt_ft != null
                    ? <>{Math.round(a.alt_ft).toLocaleString()} ft<AltitudeTrend rate={a.baro_rate} /></>
                    : "Alt n/a"}
                  {a.gs_kt != null && <> · {Math.round(a.gs_kt)} kt</>}
                  {a.track != null && <> · hdg {Math.round(a.track)}°</>}
                </div>
                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4, display: "flex", justifyContent: "space-between" }}>
                  <span>ADS-B · {a.registration || a.hex}</span>
                  <SignalAge seenS={a.seen_s} baseMs={baseMsRef.current} />
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
